<script>
import bem from '@/utils/bem';
import { mapActions, mapGetters, mapState } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import toggleModal from '@/mixins/toggleModal';
import notificationWithIcon from '@/utils/notification';
import {
  DownOutlined,
  UpOutlined,
  LeftOutlined,
  RightOutlined, DeleteOutlined,
} from '@ant-design/icons-vue';

const b = bem('change-delivery-modal');
export default {
  components: {
    DeleteOutlined,
    DownOutlined,
    UpOutlined,
    LeftOutlined,
    RightOutlined,
  },
  data() {
    return {
      dependQuantityProductIds: [],
      shippingSetting: {},
      availableDependsList: [],
      sales: [],
      error: {
        field: '',
        text: '',
      },
      errorSales: [],
      selectorOptions: [],
      isShowSettingsDependOptions: false,
      displayedListId: null,
      idChangeableDependProducts: null,
      companyProduct: null,
      companyProductData: {
        displayName: '',
        displayText: '',
        hint: '',
        subName: '',
        localZipCodes: false,
        localPickUp: false,
      },
      selectedCompanyId: null,
      activeMainTab: 'basic',
      formData: {
        displayName: '',
        subName: '',
        displayText: '',
        hint: '',
        price: null,
        additionalDiscount: null,
      },
    };
  },
  props: {
    delivery: {
      displayName: String,
      subName: String,
      displayText: String,
      price: Number,
      additionalDiscount: Number,
      imageUri: String,
      ext: {
        shippingSetting: {
          activeCreate: Boolean,
          activeDigitize: Boolean,
          activeEnhance: Boolean,
          activeGiftCard: Boolean,
          displayAtCheckout: Boolean,
          recycle: Boolean,
          localPickUp: Boolean,
          localZipCodes: Boolean,
        },
      },
    },
    type: {
      type: String,
      required: true,
    },
    deliveryKey: {
      type: String,
      required: true,
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  mixins: [toggleModal],
  async mounted() {
    // Fetch companies for displaying company names in options
    await this.getCompaniesAction({ limit: 1000, offset: 0 });

    // Initialize form data from delivery prop
    this.formData = {
      displayName: this.delivery.displayName || '',
      subName: this.delivery.subName || '',
      displayText: this.delivery.displayText || '',
      hint: this.delivery.hint || '',
      price: this.delivery.price ?? null,
      additionalDiscount: this.delivery.additionalDiscount ?? null,
    };

    this.shippingSetting = {
      activeCreate: this.delivery.ext.shippingSetting.activeCreate,
      activeDigitize: this.delivery.ext.shippingSetting.activeDigitize,
      activeEnhance: this.delivery.ext.shippingSetting.activeEnhance,
      activeGiftCard: this.delivery.ext.shippingSetting.activeGiftCard,
      displayAtCheckout: this.delivery.ext.shippingSetting.displayAtCheckout,
      recycle: this.delivery.ext.shippingSetting.recycle,
      localPickUp: this.delivery.ext.shippingSetting.localPickUp,
      localZipCodes: this.delivery.ext.shippingSetting.localZipCodes,
    };
    this.sales = this.delivery?.sales?.map((item) => ({
      ...item,
      settings: item?.settings?.map((setting) => ({
        ...setting,
        id: uuidv4(),
      })) || [],
    })) || [];

    const products = this.allProducts.reduce((acc, product) => {
      const {
        type,
        productOptions = [],
      } = product;
      if (type && (type === 'digitize' || type === 'enhance')) {
        const newOptions = productOptions && productOptions.length && type === 'digitize' ? productOptions.map((val) => ({
          ...val,
          applyToOptions: false,
        })) : [];

        const digitizeAndEnhanceProducts = [
          {
            ...product,
            applyToOptions: true,
          },
          ...newOptions,
        ];
        return [...acc, ...digitizeAndEnhanceProducts];
      }
      return acc;
    }, []);

    const photoArtProducts = this.allProducts.reduce((acc, item) => {
      if (item.type && (item.type === 'photo_art' || item.type === 'collage')) {
        return [...acc, {
          ...item,
          value: item.value,
          applyToOptions: true,
          label: item.label,
        }];
      }
      return acc;
    }, []);

    this.selectorOptions = [...products, ...photoArtProducts].filter((product) => {
      if (this.deliveryKey === 'digitize' || this.deliveryKey === 'enhance') {
        return product.type === this.deliveryKey;
      }
      if (this.deliveryKey === 'create') {
        return product.type === 'photo_art' || product.type === 'collage';
      }
      if (this.deliveryKey === 'enhanceCreate') {
        return product.type === 'photo_art' || product.type === 'collage' || product.type === 'enhance';
      }
      return product.type === this.deliveryKey;
    });

    if (this.companyId) {
      this.selectedCompanyId = this.companyId;
    }
  },
  computed: {
    ...mapGetters({
      allProducts: 'products/allProducts',
    }),
    ...mapState({
      companies: (state) => state.companies.companies,
    }),
    companiesOptions() {
      return this.companies.map((company) => ({
        label: company.displayName,
        value: company.id,
      }));
    },
    filterSelectorOptions() {
      const {
        idChangeableDependProducts,
        sales,
        selectorOptions,
      } = this;

      const result = selectorOptions.reduce((acc, option) => {
        const alreadySelected = sales[idChangeableDependProducts]?.dependsIds || [];

        const filteredOptions = (option.productOptions || []).filter((productOption) => {
          const { value, isMatting: isMattingOption } = productOption;
          const isAlreadySelected = alreadySelected.includes(value);
          const shouldInclude = !isAlreadySelected && !isMattingOption;

          return shouldInclude;
        });

        const updatedOption = {
          ...option,
          productOptions: filteredOptions,
        };

        const { frameMaterial } = updatedOption;
        const { name: frameMaterialName = 'otherProducts' } = frameMaterial || {};

        return {
          ...acc,
          [frameMaterialName]: [...(acc[frameMaterialName] || []), updatedOption],
        };
      }, {});

      return result;
    },
    getSelectedDependProducts() {
      const selectedValuesSet = new Set(this.sales[this.idChangeableDependProducts].dependsIds);

      const filterProductOptions = (productOptions = []) => productOptions.reduce((acc, option) => {
        if (selectedValuesSet.has(option.value) && !option.isMatting) {
          return [...acc, {
            ...option,
            productOptions: filterProductOptions(option.productOptions),
          }];
        }
        return acc;
      }, []);

      return this.selectorOptions.reduce((acc, option) => {
        if (!option.parentId) {
          const productOptions = filterProductOptions(option.productOptions || []);
          const newOption = (productOptions.length) ? {
            ...option,
            productOptions,
          } : { label: option.label };

          const { frameMaterial } = option;
          const { name: frameMaterialName = 'otherProducts' } = frameMaterial || {};
          return {
            ...acc,
            [frameMaterialName]: [...(acc[frameMaterialName] || []), newOption],
          };
        }
        return acc;
      }, {});
    },
  },
  watch: {
    async selectedCompanyId(newCompanyId) {
      if (!newCompanyId) {
        this.companyProduct = null;
        this.companyProductData.displayName = '';
        this.companyProductData.displayText = '';
        this.companyProductData.hint = '';
        this.companyProductData.subName = '';
        this.companyProductData.localZipCodes = false;
        this.companyProductData.localPickUp = false;
        return;
      }

      try {
        const companyProducts = await this.getCompanyProductsByCompanyIdAction(newCompanyId);

        const matchingProduct = companyProducts.find(
          (product) => product.productId === this.delivery.id,
        );

        if (matchingProduct) {
          this.companyProduct = matchingProduct;
          this.companyProductData.displayName = matchingProduct.displayName || '';
          this.companyProductData.displayText = matchingProduct.displayText || '';
          this.companyProductData.hint = matchingProduct.hint || '';
          this.companyProductData.subName = matchingProduct.subName || '';
          this.companyProductData.localZipCodes = matchingProduct.localZipCodes || false;
          this.companyProductData.localPickUp = matchingProduct.localPickUp || false;
        } else {
          this.companyProduct = null;
          this.companyProductData.displayName = this.delivery.displayName || '';
          this.companyProductData.displayText = this.delivery.displayText || '';
          this.companyProductData.hint = this.delivery.hint || '';
          this.companyProductData.subName = this.delivery.subName || '';
          this.companyProductData.localZipCodes = this.delivery.ext?.shippingSetting?.localZipCodes
          || false;
          this.companyProductData.localPickUp = this.delivery.ext?.shippingSetting?.localPickUp
          || false;
        }
      } catch (err) {
        this.companyProduct = null;
        this.companyProductData.displayName = this.delivery.displayName || '';
        this.companyProductData.displayText = this.delivery.displayText || '';
        this.companyProductData.hint = this.delivery.hint || '';
        this.companyProductData.subName = this.delivery.subName || '';
        this.companyProductData.localZipCodes = this.delivery.ext?.shippingSetting?.localZipCodes
        || false;
        this.companyProductData.localPickUp = this.delivery.ext?.shippingSetting?.localPickUp
        || false;
      }
    },
    sales: {
      handler(newValue) {
        this.errorSales = newValue.map((val) => val.settings.reduce((acc, item, index) => {
          const {
            fromQuantity,
            toQuantity,
            price,
            additionalDiscount,
          } = item;
          const isEmptyFields = fromQuantity === null
            || toQuantity === null
            || price === null
            || additionalDiscount === null;
          if (isEmptyFields) {
            return [...acc, {
              field: 'all',
              text: 'One of field is empty',
              index,
            }];
          }
          const isWrongQuantity = (
            fromQuantity > toQuantity
            || newValue.some(
              (itm) => fromQuantity >= itm.fromQuantity
                && fromQuantity <= itm.toQuantity
                && itm.id !== item.id,
            ) || newValue.some(
              (itm) => toQuantity >= itm.fromQuantity
                && fromQuantity <= itm.toQuantity
                && itm.id !== item.id,
            )
          );
          if (isWrongQuantity) {
            return [...acc, {
              field: 'quantity',
              text: 'The number overlaps with other ranges',
              index,
            }];
          }
          const isWrongDiscount = item.additionalDiscount > item.price;
          if (isWrongDiscount) {
            return [...acc, {
              field: 'additionalDiscount',
              text: 'Discount more than price',
              index,
            }];
          }
          return [...acc, {
            field: '',
            text: '',
          }];
        }, []));
      },
      deep: true,
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      updateDeliveryAction: 'products/updateProduct',
      getCompanyProductByProductIdAction: 'products/getCompanyProductByProductId',
      getCompanyProductsByCompanyIdAction: 'products/getCompanyProductsByCompanyId',
      createCompanyProductAction: 'products/createCompanyProduct',
      updateCompanyProductAction: 'products/updateCompanyProduct',
      getCompaniesAction: 'companies/getCompanies',
    }),
    handleOk() {
      if (
        this.error?.field
        || this.errorSales.flatMap((item) => item)
          .filter((item) => item.field).length
      ) {
        return null;
      }

      const newValue = {
        id: this.delivery.id,
        type: this.type,
        ext: {
          shippingSettings: {
            ...this.shippingSetting,
          },
        },
        imageUri: this.delivery.imageUri,
        price: this.formData.price !== null ? Number(this.formData.price) : null,
        additionalDiscount: this.formData.additionalDiscount !== null
          ? Number(this.formData.additionalDiscount) : null,
        displayText: this.formData.displayText ? String(this.formData.displayText) : '',
        displayName: String(this.formData.displayName),
        subName: this.formData.subName ? String(this.formData.subName) : '',
        hint: this.formData.hint ? String(this.formData.hint) : '',
      };

      newValue.sales = this.sales?.map(({
        id,
        productId,
        ...item
      }) => ({
        ...item,
        settings: item.settings.map(({
          id: _,
          ...rest
        }) => (rest)),
      }));
      this.updateDeliveryAction(newValue);
      this.hideModal();
      return null;
    },
    onDeleteBulk(id, index) {
      this.sales[index].settings = this.sales?.[index]?.settings?.filter((item) => item.id !== id);
    },
    onDeleteOneSales(id) {
      this.sales = this.sales?.filter((item) => item.id !== id);
    },
    addSales() {
      this.sales = [
        ...this.sales,
        {
          applyToOptions: false,
          dependsIds: [],
          settings: [],
          displayName: '',
        },
      ];
    },
    addBulk(sales, index) {
      const { settings = [] } = sales;
      const toQuantityPrevSales = settings?.[settings.length - 1 || 0]?.toQuantity || 0;

      this.sales[index].settings = [
        ...settings,
        {
          id: uuidv4(),
          fromQuantity: toQuantityPrevSales + 1,
          toQuantity: toQuantityPrevSales + 1,
          price: 1,
          additionalDiscount: 0,
          isPercentage: false,
        },
      ];
    },
    showEditDependent(sale, index) {
      this.idChangeableDependProducts = index;
      this.isShowSettingsDependOptions = !this.isShowSettingsDependOptions;
    },
    changeToggle(field, value) {
      this.shippingSetting = {
        ...this.shippingSetting,
        [field]: value,
      };
    },
    addDependBulkProducts({
      product,
      parentProduct,
      applyAll,
    }) {
      const { dependsIds } = this.sales[this.idChangeableDependProducts];
      const allOptions = applyAll ? product.productOptions.map((itm) => itm.value) : [];
      this.sales[this.idChangeableDependProducts].dependsIds = parentProduct
        ? [...(
          new Set([
            ...dependsIds,
            product.value,
          ])
        )] : [...(
          new Set([
            ...dependsIds,
            ...allOptions,
          ])
        )];
    },
    addAllGroupedDependBulkProducts(products) {
      this.sales[this.idChangeableDependProducts].dependsIds = [...(
        new Set([
          ...this.sales[this.idChangeableDependProducts].dependsIds,
          ...products.reduce((acc, itm) => (
            [...acc, itm.value, ...(itm.productOptions || []).map((option) => option.value)]
          ), []),
        ])
      )];
    },
    removeDependBulkProducts({
      product,
      applyAll,
    }) {
      const { dependsIds } = this.sales[this.idChangeableDependProducts];
      const allOptions = applyAll ? product.productOptions.map((itm) => itm.value) : [];
      if (applyAll) {
        this.sales[this.idChangeableDependProducts].dependsIds = dependsIds.filter(
          (value) => value !== product.value && !allOptions.includes(value),
        );
      } else {
        this.sales[this.idChangeableDependProducts].dependsIds = dependsIds.filter(
          (value) => value !== product.value,
        );
      }
    },
    removeAllGroupedDependBulkProducts(products) {
      const { dependsIds } = this.sales[this.idChangeableDependProducts];
      this.sales[this.idChangeableDependProducts].dependsIds = dependsIds.filter(
        (value) => !(products.reduce((acc, itm) => (
          [...acc, itm.value, ...(itm.productOptions || []).map((option) => option.value)]
        ), []).includes(value)),
      );
    },
    closeDependList() {
      this.isShowSettingsDependOptions = !this.isShowSettingsDependOptions;
      this.displayedListId = null;
    },
    async handleSaveCompanyProduct() {
      if (!this.selectedCompanyId) {
        notificationWithIcon('error', 'Please select a company');
        return;
      }

      const payload = {
        displayName: this.companyProductData.displayName || this.delivery.displayName || null,
        displayText: this.companyProductData.displayText || this.delivery.displayText || null,
        hint: this.companyProductData.hint || this.delivery.hint || null,
        subName: this.companyProductData.subName || this.delivery.subName || null,
        localZipCodes: this.companyProductData.localZipCodes,
        localPickUp: this.companyProductData.localPickUp,
      };

      try {
        if (this.companyProduct && this.companyProduct.id) {
          const updated = await this.updateCompanyProductAction({
            id: this.companyProduct.id,
            ...payload,
          });
          this.companyProduct = updated;
        } else {
          payload.companyId = this.selectedCompanyId;
          payload.productId = this.delivery.id;
          const created = await this.createCompanyProductAction(payload);
          this.companyProduct = created;
        }
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<template>
  <a-modal
    width="fit-content"
    :open="show"
    :footer="null"
    @cancel="hideModal"
  >
    <div :class="b('modal')" v-if="isShowSettingsDependOptions">
      <h1 :class="b('modal-title')">
        Settings depend options
        <a-button
          type="primary"
          @click="closeDependList"
        >
          Close editor
        </a-button>
      </h1>
      <div :class="b('selector-wrapper')">
        <div :class="b('selector-depends')">
          <div
            :class="b('product-wrapper', {blue: true})"
            v-for="([key, value]) in Object.entries(filterSelectorOptions)" :key="key"
          >
            <h2 :class="b('collection-grouped-name')" v-if="key !== 'otherProducts'">
              {{ key }}
              <a-button
                :class="b('collection-btn')"
                @click="addAllGroupedDependBulkProducts(value)"
              >
                All
                <RightOutlined/>
              </a-button>
            </h2>
            <div :class="b('product-wrapper')" v-for="(item, index) in value" :key="index">
              <h3 :class="b('product-name')">
                <a-button
                  @click="displayedListId = displayedListId !== item.value ? item.value : null"
                >
                  {{ item.label }}
                  <UpOutlined v-if="item.value === displayedListId"/>
                  <DownOutlined v-else/>
                </a-button>
              </h3>
              <div
                v-if="item.value === displayedListId"
                :class="b('product-options')"
              >
                <a-button
                  v-if="item?.productOptions?.length"
                  :class="b('all-product-btn')"
                  @click="addDependBulkProducts({
                  product: item,
                  applyAll: true,
                })"
                >
                  All options
                  <RightOutlined/>
                </a-button>
                <a-button
                  :class="b('option')"
                  v-for="(option, index) in item.productOptions" :key="index"
                  @click="addDependBulkProducts({
                  product: option,
                  parentProduct: item,
                })"
                  type="dashed"
                >
                  {{ option.label }}
                  <RightOutlined/>
                </a-button>
              </div>
            </div>
          </div>
        </div>
        <div :class="b('selected-depends')">
          <div
            :class="b('product-wrapper', {blue: true})"
            v-for="([key, value]) in Object.entries(getSelectedDependProducts)" :key="key"
          >
            <h2 :class="b('collection-grouped-name')" v-if="key !== 'otherProducts'">
              {{ key }}
              <a-button
                :class="b('collection-btn')"
                @click="removeAllGroupedDependBulkProducts(value)"
              >
                <LeftOutlined/>
                All
              </a-button>
            </h2>
            <div :class="b('product-wrapper')" v-for="(item, index) in value" :key="index">
              <template v-if="item.value">
                <h3 :class="b('product-name')">
                  <a-button
                    @click="displayedListId = displayedListId !== item.value ? item.value : null"
                  >
                    {{ item.label }}
                    <UpOutlined v-if="item.value === displayedListId"/>
                    <DownOutlined v-else/>
                  </a-button>
                </h3>
                <div
                  v-if="item.value === displayedListId"
                  :class="b('product-options')"
                >
                  <a-button
                    :class="b('all-product-btn')"
                    v-if="item?.productOptions?.length"
                    @click="removeDependBulkProducts({
                      product: item,
                      applyAll: true,
                    })">
                    <LeftOutlined/>
                    All options
                  </a-button>
                  <a-button
                    :class="b('option')"
                    v-for="(option, index) in item.productOptions" :key="index"
                    type="dashed"
                    @click="removeDependBulkProducts({
                      product: option,
                    })"
                  >
                    <LeftOutlined/>
                    {{ option.label }}
                  </a-button>
                </div>
              </template>
              <h3 v-else :class="b('product-name')">Product {{ item.label }} has not been added</h3>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :class="b('modal')" v-else-if="delivery">
      <FormKit
        type="form"
        submit-label="Save"
        @submit="handleOk"
        :disabled="Boolean(error.text)"
      >
        <h1 class="text-2xl font-bold mb-4">{{ delivery?.displayName }}</h1>

        <a-tabs v-model:activeKey="activeMainTab" :class="b('main-tabs')">
          <!-- Basic Info Tab -->
          <a-tab-pane key="basic" tab="Basic Info" v-if="!companyId">
            <div :class="b('tab-content')">
              <div :class="b('row')">
                <div :class="b('input', { wide: true })">
                  <FormKit
                    type="text"
                    name="displayName"
                    label="Display name"
                    validation="required"
                    placeholder="Display name"
                    v-model="formData.displayName"
                  />
                </div>
              </div>
              <div :class="b('row')">
                <div :class="b('input', { wide: true })">
                  <FormKit
                    type="text"
                    name="subName"
                    label="Sub name"
                    placeholder="Sub Name"
                    v-model="formData.subName"
                  />
                </div>
              </div>
              <div :class="b('row')">
                <div :class="b('input', { wide: true })">
                  <FormKit
                    type="text"
                    name="displayText"
                    label="Display Text"
                    placeholder="Display Text"
                    v-model="formData.displayText"
                  />
                </div>
              </div>
              <div :class="b('row')">
                <div :class="b('input', { wide: true })">
                  <FormKit
                    type="text"
                    name="hint"
                    label="Hint text"
                    validation="required"
                    placeholder="Hint text"
                    v-model="formData.hint"
                  />
                </div>
              </div>
            </div>
          </a-tab-pane>

          <!-- Pricing Tab -->
          <a-tab-pane key="pricing" tab="Pricing" v-if="!companyId">
            <div :class="b('tab-content')">
              <div :class="b('row')">
                <div :class="b('input')">
                  <FormKit
                    type="number"
                    name="price"
                    label="Default price"
                    placeholder="Price"
                    prefix="dollar"
                    v-model="formData.price"
                  />
                </div>
                <div :class="b('input')">
                  <FormKit
                    type="number"
                    name="additionalDiscount"
                    label="Default discount"
                    placeholder="Additional discount"
                    prefix="dollar"
                    v-model="formData.additionalDiscount"
                  />
                </div>
              </div>
            </div>
          </a-tab-pane>

          <!-- Settings Tab -->
          <a-tab-pane key="settings" tab="Settings" v-if="!companyId">
            <div :class="b('tab-content')">
              <h3>Checkout</h3>
              <div :class="b('settings-row')">
                <div :class="b('toggle')">
                  <span>Display at checkout</span>
                  <a-switch
                    checked-children="Yes"
                    un-checked-children="No"
                    :checked="shippingSetting.displayAtCheckout"
                    @change="changeToggle('displayAtCheckout', !shippingSetting.displayAtCheckout)"
                  />
                </div>
                <div :class="b('toggle')">
                  <span>Recycle</span>
                  <a-switch
                    checked-children="Yes"
                    un-checked-children="No"
                    :checked="shippingSetting.recycle"
                    @change="changeToggle('recycle', !shippingSetting.recycle)"
                  />
                </div>
              </div>

              <a-divider>Local</a-divider>
              <div :class="b('settings-row')">
                <div :class="b('toggle')">
                  <span>Local pick up</span>
                  <a-switch
                    checked-children="Yes"
                    un-checked-children="No"
                    :checked="shippingSetting.localPickUp"
                    @change="changeToggle('localPickUp', !shippingSetting.localPickUp)"
                  />
                </div>
                <div :class="b('toggle')">
                  <span>Local zip codes</span>
                  <a-switch
                    checked-children="Yes"
                    un-checked-children="No"
                    :checked="shippingSetting.localZipCodes"
                    @change="changeToggle('localZipCodes', !shippingSetting.localZipCodes)"
                  />
                </div>
              </div>

              <a-divider>Active</a-divider>
              <div :class="b('settings-row')">
                <div :class="b('toggle')">
                  <span>Digitize</span>
                  <a-switch
                    checked-children="Yes"
                    un-checked-children="No"
                    :checked="shippingSetting.activeDigitize"
                    @change="changeToggle('activeDigitize', !shippingSetting.activeDigitize)"
                  />
                </div>
                <div :class="b('toggle')">
                  <span>Enhance</span>
                  <a-switch
                    checked-children="Yes"
                    un-checked-children="No"
                    :checked="shippingSetting.activeEnhance"
                    @change="changeToggle('activeEnhance', !shippingSetting.activeEnhance)"
                  />
                </div>
                <div :class="b('toggle')">
                  <span>Create</span>
                  <a-switch
                    checked-children="Yes"
                    un-checked-children="No"
                    :checked="shippingSetting.activeCreate"
                    @change="changeToggle('activeCreate', !shippingSetting.activeCreate)"
                  />
                </div>
                <div :class="b('toggle')">
                  <span>Gift Card</span>
                  <a-switch
                    checked-children="Yes"
                    un-checked-children="No"
                    :checked="shippingSetting.activeGiftCard"
                    @change="changeToggle('activeGiftCard', !shippingSetting.activeGiftCard)"
                  />
                </div>
              </div>
            </div>
          </a-tab-pane>

          <!-- Sales Tab -->
          <a-tab-pane key="sales" tab="Sales" v-if="deliveryKey !== 'position' && !companyId">
            <div :class="b('tab-content')">
              <div
                :class="b('one-sales-block')"
                v-for="(item, index) in sales"
                :key="item.id"
              >
                <div :class="b('title-sales-wrapper')">
                  <a-input
                    v-model:value="item.displayName"
                    :placeholder="'Sale ' + (index + 1)"
                    :class="b('sales-name-input')"
                  />
                  <a-button
                    type="primary"
                    @click="onDeleteOneSales(item.id)"
                    danger
                    shape="round"
                    size="small"
                  >
                    <template #icon>
                      <DeleteOutlined/>
                    </template>
                  </a-button>
                </div>
                <div
                  :class="b('depends-products')"
                  v-if="item?.settings?.length"
                >
                  <a-button
                    :class="b('edit-dependent-button')"
                    danger
                    @click="showEditDependent(item, index)"
                  >
                    Click to edit dependent products and options
                    ({{ item.dependsIds?.length || 0 }} selected)
                  </a-button>
                </div>
                <template
                  v-for="(setting, settingIndex) in item.settings"
                  :key="setting.id"
                >
                  <div :class="b('one-bulk')">
                    <div :class="b('one-bulk-quantity-block')">
                      <a-input-number
                        v-model:value="setting.fromQuantity"
                        :min="(item.settings?.[(settingIndex || 2) - 1]?.toQuantity || 0) + 1"
                        :max="99999"
                        placeholder="Min"
                        :status="
                          errorSales[index]?.[settingIndex]?.field === 'quantity'
                          || setting.fromQuantity === null ? 'error' : ''
                        "
                      />
                      <div>-</div>
                      <a-input-number
                        v-model:value="setting.toQuantity"
                        :min="(item.settings?.[(settingIndex || 2) - 1]?.toQuantity || 0) + 1"
                        :max="99999"
                        placeholder="Max"
                        :status="
                          errorSales[index]?.[settingIndex]?.field === 'quantity'
                          || setting.toQuantity === null ? 'error' : ''
                        "
                      />
                    </div>
                    <div :class="b('one-bulk-price-block')">
                      <div :class="b('one-bulk-price-title')">Price</div>
                      <a-input-number
                        v-model:value="setting.price"
                        :min="0"
                        :max="99999"
                        prefix="$"
                        placeholder="Price"
                        :status="setting.price === null ? 'error' : ''"
                      />
                    </div>
                    <div :class="b('one-bulk-price-block')">
                      <div :class="b('one-bulk-price-title')">Discount</div>
                      <a-input-number
                        v-model:value="setting.additionalDiscount"
                        :min="0"
                        :max="99999"
                        prefix="$"
                        placeholder="Discount"
                        :status="
                          errorSales[index]?.[settingIndex]?.field === 'additionalDiscount'
                          || setting.additionalDiscount === null ? 'error' : ''
                        "
                      />
                    </div>
                    <div :class="b('one-bulk-percent-block')">
                      <div :class="b('one-bulk-percent-title')">%</div>
                      <a-switch v-model:checked="setting.isPercentage" size="small" />
                    </div>
                    <a-button
                      type="primary"
                      @click="onDeleteBulk(setting.id, index)"
                      danger
                    >
                      Delete
                    </a-button>
                  </div>
                  <div
                    :class="b('error')"
                    v-if="errorSales[index]?.[settingIndex]?.text"
                  >{{ errorSales[index][settingIndex].text }}</div>
                </template>
                <a-button
                  type="dashed"
                  @click="addBulk(item, index)"
                >
                  + Interval
                </a-button>
              </div>
              <a-button
                type="dashed"
                @click="addSales"
              >
                + Add Sales Block
              </a-button>
            </div>
          </a-tab-pane>

          <!-- Company Settings Tab -->
          <a-tab-pane key="company" tab="Company Settings" v-if="companyId">
            <div :class="b('tab-content')">
              <div :class="b('company-form')">
                <div :class="b('form-field')">
                  <div :class="b('field-label')">Display Name</div>
                  <a-input
                    v-model:value="companyProductData.displayName"
                    placeholder="Display name"
                  />
                </div>
                <div :class="b('form-field')">
                  <div :class="b('field-label')">Display Text</div>
                  <a-textarea
                    v-model:value="companyProductData.displayText"
                    placeholder="Display text"
                    :rows="3"
                  />
                </div>
                <div :class="b('form-field')">
                  <div :class="b('field-label')">Hint</div>
                  <a-input
                    v-model:value="companyProductData.hint"
                    placeholder="Hint text"
                  />
                </div>
                <div :class="b('form-field')">
                  <div :class="b('field-label')">Sub Name</div>
                  <a-input
                    v-model:value="companyProductData.subName"
                    placeholder="Sub name"
                  />
                </div>
                <a-button
                  type="primary"
                  @click="handleSaveCompanyProduct"
                  :disabled="!selectedCompanyId"
                  style="width: 100%; margin-top: 20px;"
                >
                  {{ companyProduct ? 'Update Company Product' : 'Create Company Product' }}
                </a-button>
              </div>
            </div>
          </a-tab-pane>
        </a-tabs>
      </FormKit>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/media";

// Override FormKit wrapper styles
:deep(.formkit-outer) {
  width: 100%;
}

:deep(.formkit-wrapper) {
  width: 100%;
  max-width: 100%;
}

:deep(.formkit-inner) {
  width: 100%;
}

:deep(.formkit-input) {
  width: 100%;
}

.change-delivery-modal {
  &__modal {
    min-width: 700px;
    max-width: 900px;
    padding: 20px;
  }

  &__main-tabs {
    margin-bottom: 16px;
  }

  &__tab-content {
    padding: 8px 0;
    min-height: 200px;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 16px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  &__input {
    width: 100%;
    display: flex;
    flex-direction: column;

    &--wide {
      width: 100%;
    }
  }

  &__image-section {
    margin-top: 12px;
  }

  &__settings-row {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 24px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  &__toggle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-width: 120px;

    span {
      font-size: 13px;
      color: #666;
      text-align: center;
    }
  }

  &__company-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  &__form-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  &__field-label {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 5px;
  }

  &__one-sales-block {
    display: flex;
    flex-direction: column;
    padding: 20px;
    border-radius: 10px;
    background-color: #d1e6f6;
    margin-bottom: 20px;
    gap: 10px;
  }

  &__title-sales-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;

    h3 {
      margin: 0;
    }
  }

  &__title-sales {
    margin: 0;
    font-weight: 600;
  }

  &__sales-name-input {
    max-width: 300px;
    font-weight: 600;
  }

  &__depends-products {
    margin: 10px 0;
  }

  &__edit-dependent-button {
    width: 100%;
  }

  &__one-bulk {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 10px;
    padding: 10px;
    background-color: #7eb4d3;
    border-radius: 8px;
  }

  &__one-bulk-quantity-block {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  &__one-bulk-price-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__one-bulk-price-title {
    font-size: 12px;
    color: #666;
  }

  &__one-bulk-percent-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__one-bulk-percent-title {
    font-size: 12px;
    color: #666;
  }

  &__error {
    color: $burnt-sienna-color;
    margin-bottom: 10px;
  }

  // Modal editor styles
  &__modal-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__selector-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  &__selector-depends,
  &__selected-depends {
    display: flex;
    flex-direction: column;
    gap: 30px;
    border-radius: 10px;
    border: 1px solid $jagged_ice;
  }

  &__product-wrapper {
    display: flex;
    min-height: 62px;
    flex-direction: column;
    gap: 15px;
    box-shadow: 10.1px 12.4px 76px 0 rgba(0, 0, 0, 0.07);
    padding: 15px;
    border-radius: 10px;
    background-color: #7eb4d3;

    &_blue {
      background-color: #d1e6f6;
    }
  }

  &__product-name {
    display: flex;
    gap: 5px;
    margin-bottom: 0;
    height: 32px;
  }

  &__product-options {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }

  &__all-product-btn {
    width: 100%;
  }

  &__collection-grouped-name {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 10px;
  }
}
</style>
